import { Component, OnInit, OnDestroy, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { MeetingsService } from '../meetings.service';

export interface Meeting {
  meeting_id: number;
  meeting_title: string;
  meeting_date?: string;
  meeting_lieu?: string;
  meeting_category?: string;
  meeting_status: 'ACTI' | 'INAC';
  meeting_status_label?: string;
  photo?: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

@Component({
  selector: 'app-meetings-list',
  standalone: false,
  templateUrl: './meetings-list.component.html',
  styleUrls: ['./meetings-list.component.scss']
})
export class MeetingsListComponent implements OnInit, OnDestroy {
  meetings = signal<Meeting[]>([]);
  isLoading = signal(true);
  isFiltering = signal(false);
  errorMessage = signal<string | null>(null);
  searchQuery = signal('');
  selectedStatus = signal('');
  pagination = signal<Pagination>({ page: 1, limit: 10, total: 0, totalPages: 0 });

  pageNumbers = computed((): (number | '...')[] => {
    const { page, totalPages } = this.pagination();
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages: (number | '...')[] = [1];
    if (page > 3) pages.push('...');
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
    if (page < totalPages - 2) pages.push('...');
    pages.push(totalPages);
    return pages;
  });

  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();

  constructor(private meetingsService: MeetingsService, private router: Router) {}

  ngOnInit(): void {
    this.searchSubject.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(query => {
      this.searchQuery.set(query);
      this.loadMeetings(1, true);
    });
    this.loadMeetings(1);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.searchSubject.complete();
  }

  private loadMeetings(page: number, filtering = false): void {
    filtering ? this.isFiltering.set(true) : this.isLoading.set(true);
    this.errorMessage.set(null);

    this.meetingsService.getMeetings(page, this.pagination().limit, this.searchQuery(), this.selectedStatus())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.meetings.set(response.data || []);
          const total = response.pagination?.total || response.total || 0;
          this.pagination.set({
            page,
            limit: this.pagination().limit,
            total,
            totalPages: Math.ceil(total / this.pagination().limit),
          });
          this.isLoading.set(false);
          this.isFiltering.set(false);
        },
        error: (err) => {
          this.errorMessage.set(err?.error?.message || 'Erreur lors du chargement des réunions');
          this.isLoading.set(false);
          this.isFiltering.set(false);
        }
      });
  }

  onSearch(value: string): void { this.searchSubject.next(value); }

  onStatusFilter(value: string): void {
    this.selectedStatus.set(value);
    this.loadMeetings(1, true);
  }

  onPageChange(page: number): void {
    const { totalPages } = this.pagination();
    if (page < 1 || page > totalPages) return;
    this.loadMeetings(page);
  }

  onAddNew(): void { this.router.navigate(['/federation/reunions/new']); }
  onView(id: number): void { this.router.navigate(['/federation/reunions', id]); }

  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.style.display = 'none';
    const parent = img.parentElement;
    if (parent) {
      parent.innerHTML = '<svg class="w-5 h-5 text-stone-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" /></svg>';
    }
  }

  statusLabel(status: string): string {
    const map: Record<string, string> = { ACTI: 'En ligne', INAC: 'Hors ligne' };
    return map[status] || status;
  }

  statusBadgeClass(status: string): string {
    if (status === 'ACTI') return 'bg-emerald-50 text-emerald-600 border-emerald-100';
    return 'bg-amber-50 text-amber-600 border-amber-100';
  }
}
